import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Table, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faRotateLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useStockTransfers } from '../../contextjs/StockTransferContext';
import { useUser } from '../../UserContext';
import {apiInstance, fetchAllProductsMinimalWithUnits, fetchCurrentStore, fetchStores} from '../../api/api';

const initialItem = { product: "", quantity: "", unit: "", conversion_factor: 1, availableUnits: [] };

export default function StockTransferPage() {
    const { transfers, requests, loading, loadTransfers, loadRequests, addTransfer, addRequest,
        editRequest, approveRequest, rejectRequestAction, returnTransferAction} = useStockTransfers();
    const { user } = useUser();

    const navigate = useNavigate();

    const [stores, setStores] = useState([]);
    const [currentStore, setCurrentStore] = useState(null);
    const [allProducts, setAllProducts] = useState([]);

    const [activeTab, setActiveTab] = useState('requests');
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [viewModalType, setViewModalType] = useState(null); // 'request' | 'transfer'
    const [viewRecord, setViewRecord] = useState(null);
    const [returnTransferRecord, setReturnTransferRecord] = useState(null);
    const [returnItems, setReturnItems] = useState([])
    const [items, setItems] = useState([{...initialItem}]);
    const [requestForm, setRequestForm] = useState({
        requesting_store: "", source_store: "", remarks: "",
    });
    const [transferForm, setTransferForm] = useState({
        from_store: "", to_store: "", remarks: "",
    });
    const [stockPreview, setStockPreview] = useState([]);
    const [editingRequestId, setEditingRequestId] = useState(null);
    const [transferFilters, setTransferFilters] = useState({
        start_date: "",
        end_date: "",
        transfer_id: "",
        product: "",
        status: "",
    });

    const [message, setMessage] = useState(null);
    const isAdmin = Boolean(
        user?.is_superuser ||
        user?.is_staff ||
        user?.groups?.includes?.('Admin') ||
        user?.groups?.includes?.('Administrator')
    );
    const isWarehouse = currentStore?.code === 'WH';

    const handleBack = () => {
        navigate(-1);
    };

    const warehouseStore = useMemo(
        () => (Array.isArray(stores) ? stores : []).find((s) => s?.code === 'WH') || null,
        [stores]
    );

    const storeNameById = useMemo(() => {
        const map = new Map();
        (Array.isArray(stores) ? stores : []).forEach((s) => {
            if (s?.id != null) map.set(String(s.id), s.name);
        });
        return map;
    }, [stores]);

    const productById = useMemo(() => {
        const map = new Map();
        (Array.isArray(allProducts) ? allProducts : []).forEach((p) => {
            if (p?.id != null) map.set(String(p.id), p);
        });
        return map;
    }, [allProducts]);

    const getStoreName = (storeId) => {
        if (storeId == null || storeId === '') return '-';
        return storeNameById.get(String(storeId)) || String(storeId);
    };

    const getProductName = (productId) => {
        if (productId == null || productId === '') return '-';
        const p = productById.get(String(productId));
        return p?.product_name || String(productId);
    };

    const getUnitLabel = (productId, unitId) => {
        if (!unitId) return '-';

        const product = productById.get(String(productId));
        if (!product) return String(unitId);

        // check base unit
        if (product.base_unit && String(product.base_unit.id) === String(unitId)) {
            return product.base_unit.name; //full name
        }

        // check alternative units
        const altUnit = (product.units || []).find((u) => String(u.unit?.id) === String(unitId));

        if (altUnit?.unit) {
            return altUnit.unit.name;
        }

        return String(unitId);
    };

    const formatDateTime = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString();
    };

    const openViewModal = async (type, record) => {
        setViewModalType(type);
        setViewRecord(record || null);

        if (type === 'request') {
            try {
                const res = await apiInstance.get(`/stock-transfer-requests/${record.id}/stock_preview/`);
                setStockPreview(res.data);
            } catch (err) {
                console.error('Error fetching stock preview:', err);
                setStockPreview([]);
            }
        } else {
            setStockPreview([]);
        }
        setShowViewModal(true);
    };

    useEffect(() => {
        loadTransfers();
        loadRequests();
    }, [loadTransfers, loadRequests]);

    useEffect(() => {
        let cancelled = false;

        const loadStoreContext = async () => {
            try {
                const [storesData, currentStoreData] = await Promise.all([
                    fetchStores(),
                    fetchCurrentStore(),
                ]);
                if (cancelled) return;

                setStores(Array.isArray(storesData) ? storesData : (storesData?.results || []));
                setCurrentStore(currentStoreData || null);
            } catch (error) {
                console.error('Error loading stores/current store:', error);
                if (cancelled) return;
                setStores([]);
                setCurrentStore(null);
            }
        };

        loadStoreContext();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const load = async () => {
            const data = await fetchAllProductsMinimalWithUnits();
            setAllProducts(Array.isArray(data) ? data : (data?.results || []));
        };
        load();
    }, []);

    useEffect(() => {
        if (currentStore?.id) {
            setRequestForm((prev) => ({...prev, requesting_store: currentStore.id }));
            setTransferForm((prev) => ({...prev, from_store: currentStore.id }));
        }
    }, [currentStore]);

    useEffect(() => {
        // For non-warehouse stores, requests must originate from the current store and be sent to the warehouse.
        if (!isWarehouse && currentStore?.id && warehouseStore?.id) {
            setRequestForm((prev) => ({
                ...prev,
                requesting_store: currentStore.id,
                source_store: warehouseStore.id,
            }));
        }
    }, [isWarehouse, currentStore, warehouseStore]);

    const pendingRequests = useMemo(
        () => requests.filter((request) => request.status === 'PENDING'),
        [requests]
    );

    const handleApprove = async (requestId) => {
        setMessage(null);
        try {
            const response = await approveRequest(requestId);
            const printNote = response?.print_warning ? ` Receipt print warning: ${response.print_warning}` : '';
            setMessage({ type: 'success', text: `Request approved successfully.${printNote}` });
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || 'Failed to approve request.',
            });
        }
    };

    const openReturnModal = (transfer) => {
        setReturnTransferRecord(transfer);
        setReturnItems((transfer.items || []).map((item) => ({
            item: item.id,
            quantity: "",
        })));
        setShowReturnModal(true);
    };

    const closeReturnModal = () => {
        setShowReturnModal(false);
        setReturnTransferRecord(null);
        setReturnItems([]);
    };

    const getReturnableQuantity = (item) => {
        const quantity = Number(item.quantity || 0);
        const returned = Number(item.returned_quantity || 0);
        return Math.max(0, quantity - returned);
    };

    const handleReturnQuantityChange = (itemId, quantity) => {
        setReturnItems((prev) => prev.map((row) => (
            String(row.item) === String(itemId) ? {...row, quantity} : row
        )));
    };

    const fillAllReturnQuantities = () => {
        setReturnItems((returnTransferRecord?.items || []).map((item) => ({
            item: item.id,
            quantity: getReturnableQuantity(item),
        })));
    };

    const submitReturnItems = async () => {
        const transferLabel = returnTransferRecord?.transfer_id || `TR-${returnTransferRecord?.id}`;
        const payloadItems = returnItems
            .map((row) => ({...row, quantity: Number(row.quantity || 0)}))
            .filter((row) => row.quantity > 0);

        if (!payloadItems.length) {
            setMessage({type: 'danger', text: 'Enter at least one quantity to return.'});
            return;
        }

        setMessage(null);
        try {
            await returnTransferItemsAction(returnTransferRecord.id, payloadItems);
            setMessage({ type: 'success', text: `Transfer ${transferLabel} returned successfully.` });
            closeReturnModal();
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || `Failed to return transfer ${transferLabel}.`,
            });
        }
    };

    const handleReject = async (requestId) => {
        setMessage(null);
        try {
            await rejectRequestAction(requestId);
            setMessage({ type: 'success', text: `Request #${requestId} rejected.` });
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || 'Failed to reject request.',
            });
        }
    };

    const handleItemChange = (index, field, value) => {
        const updated = [...items];
        updated[index][field] = value;
        setItems(updated);
    };

    const buildAvailableUnits = (product) => {
        const availableUnits = [];

        // base unit first (conversion = 1)
        if (product?.base_unit?.id) {
            availableUnits.push({
                id: product.base_unit.id,
                name: product.base_unit.name,
                abbreviation: product.base_unit.abbreviation,
                conversion_factor: 1,
                is_base: true,
            });
        }

        // alternative units
        if (Array.isArray(product?.units) && product.units.length) {
            product.units.forEach((u) => {
                if (!u?.unit?.id) return;
                availableUnits.push({
                    id: u.unit.id,
                    name: u.unit.name,
                    abbreviation: u.unit.abbreviation,
                    conversion_factor: u.conversion_factor,
                    is_base: false,
                });
            });
        }

        return availableUnits;
    };

    const handleProductSelect = (index, productId) => {
        const updated = [...items];

        updated[index].product = productId;

        const product = (Array.isArray(allProducts) ? allProducts : []).find(
            (p) => String(p.id) === String(productId)
        );

        const availableUnits = buildAvailableUnits(product);
        updated[index].availableUnits = availableUnits;

        // default to base unit (or the only available unit)
        const defaultUnit = availableUnits[0] || null;
        updated[index].unit = defaultUnit ? defaultUnit.id : '';
        updated[index].conversion_factor = defaultUnit ? (defaultUnit.conversion_factor || 1) : 1;

        setItems(updated);
    };

    const handleUnitSelect = (index, unitId) => {
        const updated = [...items];
        updated[index].unit = unitId;

        const selectedUnit = (updated[index].availableUnits || []).find(
            (u) => String(u.id) === String(unitId)
        );
        updated[index].conversion_factor = selectedUnit ? (selectedUnit.conversion_factor || 1) : 1;
        setItems(updated);
    };

    const addLine = () => setItems([...items, {...initialItem}]);
    const removeLine = (index) => setItems(items.filter((_, i) => i !== index));

    const resetItems = () => setItems([{...initialItem}]);

    const closeRequestModal = () => {
        setShowRequestModal(false);
        setEditingRequestId(null);
        resetItems();
    };

    const submitRequest = async (e) => {
        e.preventDefault();

        if (!isWarehouse) {
            if (!requestForm?.requesting_store || !requestForm?.source_store) {
                setMessage({
                    type: 'danger',
                    text: warehouseStore
                        ? 'Please wait for store context to load and try again.'
                        : 'Warehouse store could not be determined. Please contact an administrator.',
                });
                return;
            }
        }
        const payloadItems = items.map((it) => ({
            product: it.product,
            quantity: it.quantity,
            unit: it.unit,
            conversion_factor: it.conversion_factor,
        }));

        if (payloadItems.some((it) => !it.product || !it.quantity)) {
            setMessage({ type: 'danger', text: 'Please select product and quantity for all items.' });
            return;
        }

        try {
            if (editingRequestId) {
                await editRequest(editingRequestId, { ...requestForm, items: payloadItems });
                setMessage({ type: 'success', text: 'Transfer Request updated successfully!' });
            } else {
                await addRequest({...requestForm, items: payloadItems});
                setMessage({type: 'success', text: 'Transfer Request submitted successfully!'});
            }
            setShowRequestModal(false);
            setEditingRequestId(null);
            resetItems();
        } catch (error) {
            setMessage({ type: 'danger',
                text: error?.response?.data?.detail || 'Failed to save transfer request.'
            });
        }
    };

    const submitTransfer = async (e) => {
        e.preventDefault();
        const payloadItems = items.map((it) => ({
            product: it.product,
            quantity: it.quantity,
            unit: it.unit,
            conversion_factor: it.conversion_factor,
        }));

        if (payloadItems.some((it) => !it.product || !it.quantity || !it.unit)) {
            setMessage({ type: 'danger', text: 'Please select product, quantity, and unit for all items.' });
            return;
        }
        try {
            await addTransfer({ ...transferForm, items: payloadItems });
            setMessage({ type: 'success', text: 'Transfer completed successfully!' });
            setShowTransferModal(false);
            resetItems();
        } catch (error) {
            setMessage({ type: 'danger',
                text: error?.response?.data?.detail || 'Failed to complete transfer.'
            });
        }
    };

    const productOptions = useMemo(
        () => (Array.isArray(allProducts) ? allProducts : []).map((p) => ({
            value: p.id,
            label: p.product_name,
        })),
        [allProducts]
    );

    const openCreateRequestModal = () => {
        setEditingRequestId(null);
        resetItems();
        setRequestForm({
            requesting_store: currentStore?.id || "",
            source_store: !isWarehouse && warehouseStore?.id ? warehouseStore.id : "",
            remarks: "",
        });
        setShowRequestModal(true);
    };

    const openEditRequestModal = (requestRecord) => {
        setEditingRequestId(requestRecord.id);
        setRequestForm({
            requesting_store: requestRecord.requesting_store || "",
            source_store: requestRecord.source_store || "",
            remarks: requestRecord.remarks || "",
        });
        setItems((requestRecord.items || []).map((item) => {
            const product = (Array.isArray(allProducts) ? allProducts : []).find(
                (p) => String(p.id) === String(item.product)
            );
            return {
                product: item.product,
                quantity: item.quantity,
                unit: item.unit || "",
                conversion_factor: item.conversion_factor || 1,
                availableUnits: buildAvailableUnits(product),
            };
        }));
        setShowRequestModal(true);
    };

    const applyTransferFilters = () => loadTransfers(transferFilters);

    const clearTransferFilters = () => {
        const cleared = { start_date: "", end_date: "", transfer_id: "", product: "", status: "" };
        setTransferFilters(cleared);
        loadTransfers(cleared);
    };

    return (
        <div className="container-fluid py-4">
            <div className="back-button-container">
                <Button variant="danger" onClick={handleBack}>Back</Button>
            </div>
            <Row className="mb-4">
                <Col md={8}>
                    <h3 className="fw-bold mb-1">Stock Transfer Control Center</h3>
                    <p className="text-muted mb-0">
                        Move products between stores and approval flow
                    </p>
                </Col>
                <Col md={4} className="text-md-end mt-3 mt-md-0">
                    {!isWarehouse ? (
                        <Button
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => {
                                openCreateRequestModal();
                            }}
                        >
                            Request Transfer
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={() => {
                                resetItems();
                                setShowTransferModal(true);
                            }}
                        >
                            New Direct Transfer
                        </Button>
                    )}
                </Col>
            </Row>

            {message && (
                <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
                    {message.text}
                </Alert>
            )}

            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body>
                            <div className="text-muted small">Pending Request</div>
                            <h2 className="fw-bold mb-0">{pendingRequests.length}</h2>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                        <Nav.Item>
                            <Nav.Link eventKey="requests">Transfer Requests</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="transfers">Transfers</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <div className="pt-4">
                        {loading && <Spinner animation="border" />}

                        {activeTab === 'requests' && (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Date</th>
                                        <th>Requesting Store</th>
                                        <th>Source Store</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                        <th>View</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.length ? requests.map((req) => (
                                        <tr key={req.id}>
                                            <td>{req.request_id || `#${req.id}`}</td>
                                            <td>{formatDateTime(req.request_date)}</td>
                                            <td>{getStoreName(req.requesting_store)}</td>
                                            <td>{getStoreName(req.source_store)}</td>
                                            <td>
                                                <Badge bg={req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'secondary' : 'warning'}>
                                                    {req.status}
                                                </Badge>
                                            </td>
                                            <td>{req.remarks || "-"}</td>
                                            <td>
                                                <Button
                                                    variant="info"
                                                    size="sm"
                                                    onClick={() => openViewModal('request', req)}
                                                    title="View request items"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </Button>
                                            </td>
                                            <td>
                                                {isWarehouse && isAdmin && req.status === 'PENDING' ? (
                                                    <>
                                                        <Button
                                                            size="sm" variant="outline-secondary" className="me-2"
                                                            onClick={() => openEditRequestModal(req)}
                                                            title="Adjust request before approval"
                                                        >
                                                            <FontAwesomeIcon icon={faPen} />
                                                        </Button>
                                                        <Button
                                                            size="sm" variant="success" className="me-2"
                                                            onClick={() => handleApprove(req.id)}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="sm" variant="outline-danger"
                                                            onClick={() => handleReject(req.id)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="8" className="text-center text-muted">
                                                No transfer requests available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}

                        {activeTab === 'transfers' && (
                            <>
                            <Row className="g-3 align-items-end mb-3">
                                <Col md={2}>
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={transferFilters.start_date}
                                        onChange={(e) => setTransferFilters({...transferFilters, start_date: e.target.value})}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={transferFilters.end_date}
                                        onChange={(e) => setTransferFilters({...transferFilters, end_date: e.target.value})}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Transfer ID</Form.Label>
                                    <Form.Control
                                        value={transferFilters.transfer_id}
                                        onChange={(e) => setTransferFilters({...transferFilters, transfer_id: e.target.value})}
                                        placeholder="2026-TR-000001"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Product</Form.Label>
                                    <Select
                                        value={productOptions.find((p) => String(p.value) === String(transferFilters.product)) || null}
                                        options={productOptions}
                                        onChange={(selectedOption) =>
                                            setTransferFilters({...transferFilters, product: selectedOption ? selectedOption.value : ''})}
                                        isClearable
                                        isSearchable
                                        placeholder="Filter by product..."
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={transferFilters.status}
                                        onChange={(e) => setTransferFilters({...transferFilters, status: e.target.value})}
                                    >
                                        <option value="">All</option>
                                        <option value="COMPLETED">Completed</option>
                                        <option value="PARTIALLY_RETURNED">Partially Returned</option>
                                        <option value="RETURNED">Returned</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Button variant="primary" className="me-2" onClick={applyTransferFilters}>Filter</Button>
                                    <Button variant="outline-secondary" onClick={clearTransferFilters}>Clear</Button>
                                </Col>
                            </Row>

                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Date</th>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Status</th>
                                            <th>Remarks</th>
                                            <th>View</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transfers.length ? transfers.map((transfer) => (
                                            <tr key={transfer.id}>
                                                <td>{transfer.transfer_id || `TR-${transfer.id}`}</td>
                                                <td>{formatDateTime(transfer.transfer_date)}</td>
                                                <td>{getStoreName(transfer.from_store)}</td>
                                                <td>{getStoreName(transfer.to_store)}</td>
                                                <td>
                                                    <Badge bg={transfer.status === 'RETURNED' ? 'secondary' : transfer.status === 'PARTIALLY_RETURNED' ? 'warning' : 'success'}>
                                                        {transfer.status || 'COMPLETED'}
                                                    </Badge>
                                                </td>
                                                <td>{transfer.remarks || "-"}</td>
                                                <td>
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        onClick={() => openViewModal('transfer', transfer)}
                                                        title="View transfer items"
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </Button>
                                                </td>
                                                <td>
                                                    {isWarehouse && isAdmin && ['COMPLETED', 'PARTIALLY_RETURNED'].includes(transfer.status) ? (
                                                        <Button
                                                            variant="outline-warning"
                                                            size="sm"
                                                            onClick={() => openReturnModal(transfer)}
                                                            title="Return transfer"
                                                        >
                                                            <FontAwesomeIcon icon={faRotateLeft} />
                                                        </Button>
                                                    ) : "-"}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="8" className="text-center text-muted">
                                                    No transfers available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <ViewItemsModal
                show={showViewModal}
                onHide={() => {
                    setShowViewModal(false);
                    setViewModalType(null);
                    setViewRecord(null);
                }}
                type={viewModalType}
                record={viewRecord}
                getProductName={getProductName}
                getUnitLabel={getUnitLabel}
                stockPreview={stockPreview}
            />

            <TransferModal
                show={showRequestModal}
                onHide={closeRequestModal}
                title={editingRequestId ? "Adjust Transfer Request" : "Create Transfer Request"}
                handleSubmit={submitRequest}
                items={items}
                productOptions={productOptions}
                stores={stores}
                currentStore={currentStore}
                warehouseStore={warehouseStore}
                isWarehouse={isWarehouse}
                form={requestForm}
                setForm={setRequestForm}
                handleItemChange={handleItemChange}
                handleProductSelect={handleProductSelect}
                handleUnitSelect={handleUnitSelect}
                addLine={addLine}
                removeLine={removeLine}
                mode="request"
                submitLabel={editingRequestId ? "Save Request" : "Save"}
            />

            <TransferModal
                show={showTransferModal}
                onHide={() => setShowTransferModal(false)}
                title="Create Direct Transfer"
                handleSubmit={submitTransfer}
                items={items}
                productOptions={productOptions}
                stores={stores}
                currentStore={currentStore}
                warehouseStore={warehouseStore}
                isWarehouse={isWarehouse}
                form={transferForm}
                setForm={setTransferForm}
                handleItemChange={handleItemChange}
                handleProductSelect={handleProductSelect}
                handleUnitSelect={handleUnitSelect}
                addLine={addLine}
                removeLine={removeLine}
                mode="transfer"
            />

            <ReturnTransferModal
                show={showReturnModal}
                onHide={closeReturnModal}
                transfer={returnTransferRecord}
                returnItems={returnItems}
                getProductName={getProductName}
                getUnitLabel={getUnitLabel}
                getReturnableQuantity={getReturnableQuantity}
                onQuantityChange={handleReturnQuantityChange}
                onReturnAll={fillAllReturnQuantities}
                onSubmit={submitReturnItems}
            />
        </div>
    );
}

function ReturnTransferModal({
    show,
    onHide,
    transfer,
    returnItems,
    getProductName,
    getUnitLabel,
    getReturnableQuantity,
    onQuantityChange,
    onReturnAll,
    onSubmit,
}) {
    const items = Array.isArray(transfer?.items) ? transfer.items : [];
    const valueByItem = new Map(returnItems.map((row) => [String(row.item), row.quantity]));

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Return Transfer Items</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <strong>Transfer:</strong> {transfer?.transfer_id || (transfer?.id ? `TR-${transfer.id}` : '-')}
                </div>
                <Table responsive bordered hover>
                    <thead>
                    <tr>
                        <th>Product</th>
                        <th>Unit</th>
                        <th>Transferred</th>
                        <th>Returned</th>
                        <th>Remaining</th>
                        <th>Return Qty</th>
                    </tr>
                    </thead>
                    <tbody>
                    {items.length ? items.map((item) => {
                        const remaining = getReturnableQuantity(item);
                        return (
                            <tr key={item.id}>
                                <td>{getProductName(item.product)}</td>
                                <td>{getUnitLabel(item.product, item.unit)}</td>
                                <td>{item.quantity}</td>
                                <td>{item.returned_quantity || 0}</td>
                                <td>{remaining}</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max={remaining}
                                        step="0.01"
                                        disabled={remaining <= 0}
                                        value={valueByItem.get(String(item.id)) || ""}
                                        onChange={(event) => onQuantityChange(item.id, event.target.value)}
                                    />
                                </td>
                            </tr>
                        );
                    }) : (
                        <tr>
                            <td colSpan="6" className="text-center text-muted">No items available</td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-warning" onClick={onReturnAll}>Return All Remaining</Button>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="warning" onClick={onSubmit}>Process Return</Button>
            </Modal.Footer>
        </Modal>
    );
}

function ViewItemsModal({ show, onHide, type, record, getProductName, getUnitLabel, stockPreview}) {
    const items = Array.isArray(record?.items) ? record.items : [];

    const headerId = type === 'transfer'
        ? (record?.transfer_id || (record?.id != null ? `TR-${record.id}` : '-'))
        : (record?.request_id || (record?.id != null ? `#${record.id}` : '-'));

    const title = type === 'transfer' ? 'Transfer Details' : 'Transfer Request Details';

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <div><strong>ID:</strong> {headerId}</div>
                    <div><strong>Remarks:</strong> {record?.remarks || '-'}</div>
                </div>

                <Table responsive bordered hover>
                    <thead>
                        <tr>
                            <th style={{ width: '45%' }}>Product</th>
                            <th style={{ width: '20%' }}>Unit</th>
                            <th style={{ width: '15%' }}>Requested</th>
                            <th style={{ width: '10%' }}>Available</th>
                            <th style={{ width: '10%' }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                    {items.length ? items.map((it, idx) => {
                        const preview = stockPreview.find(p => String(p.product_id) === String(it.product));

                        return (
                            <tr key={idx}>
                                <td>{getProductName(it.product)}</td>
                                <td>{getUnitLabel(it.product, it.unit)}</td>
                                <td>{it.quantity}</td>
                                <td>{preview?.available_qty ?? '-'}</td>
                                <td>
                                    {preview ? (
                                        <Badge bg={preview.status === "ok" ? "success" : "danger"}>
                                            {preview.status}
                                        </Badge>
                                    ) : "-"}
                                </td>
                            </tr>
                        );
                    }) : (
                    <tr>
                        <td colSpan="5" className="text-center text-muted">
                            No items available
                        </td>
                    </tr>
                    )}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

function TransferModal({
       show,
       onHide,
       title,
       handleSubmit,
       items,
       productOptions,
       stores,
       currentStore,
       warehouseStore,
       isWarehouse,
       form,
       setForm,
       handleItemChange,
       handleProductSelect,
       handleUnitSelect,
       addLine,
       removeLine,
       mode,
       submitLabel = "Save"
    }) {

    const lockRequestStores = mode === 'request' && !isWarehouse;

    const requestingStoreOptions = lockRequestStores
        ? (currentStore ? [currentStore] : [])
        : stores;
    const sourceStoreOptions = lockRequestStores
        ? (warehouseStore ? [warehouseStore] : [])
        : stores;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row className="g-3 mb-3">
                        <Col md={6}>
                            <Form.Label>{mode === "request" ? "Requesting Store" : "From Store"}</Form.Label>
                                <Form.Select
                                    value={mode === "request" ? form.requesting_store : form.from_store}
                                    onChange={(e) =>
                                        setForm({...form, [mode === "request" ? "requesting_store" : "from_store"]: e.target.value})
                                    }
                                    required
                                    disabled={lockRequestStores && mode === 'request'}
                                >
                                    <option value="">Select Store</option>
                                    {requestingStoreOptions.map((store) => (
                                        <option key={store.id} value={store.id}>
                                            {store.name}
                                        </option>
                                    ))}
                                </Form.Select>
                        </Col>

                        <Col md={6}>
                            <Form.Label>{mode === "request" ? "Source Store" : "To Store"}</Form.Label>
                            <Form.Select
                                value={mode === "request" ? form.source_store : form.to_store}
                                onChange={(e) =>
                                    setForm({...form, [mode === "request" ? "source_store" : "to_store"]: e.target.value})
                                }
                                required
                                disabled={lockRequestStores && mode === 'request'}
                            >
                                <option value="">Select Store</option>
                                {sourceStoreOptions.map((store) => (
                                    <option key={store.id} value={store.id}>
                                        {store.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    {items.map((item, index) => (
                        <Card key={index} className="mb-3 border-0 shadow-sm">
                            <Card.Body>
                                <Row className="g-3 align-items-end">
                                    <Col md={6}>
                                        <Form.Label>Product</Form.Label>
                                        <Select
                                            value={productOptions.find((p) => String(p.value) === String(item.product))}
                                            options={productOptions}
                                            onChange={(selectedOption) =>
                                                handleProductSelect(index, selectedOption ? selectedOption.value : '')}
                                            isSearchable={true}
                                            placeholder="Select Product"
                                        />
                                    </Col>

                                    <Col md={3}>
                                        <Form.Label>Quantity</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={item.quantity}
                                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                            required
                                        />
                                    </Col>

                                    {Array.isArray(item.availableUnits) && item.availableUnits.length > 1 ? (
                                        <Col md={2}>
                                            <Form.Label>Unit</Form.Label>
                                            <Form.Select
                                                value={item.unit}
                                                onChange={(e) => handleUnitSelect(index, e.target.value)}
                                                required
                                            >
                                                <option value="">Select unit</option>
                                                {item.availableUnits.map((u) => (
                                                    <option key={u.id} value={u.id}>
                                                        {u.name}{u.abbreviation ? ` (${u.abbreviation})` : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                    ) : (
                                        <Col md={2}>
                                            <Form.Label>Unit</Form.Label>
                                            <Form.Control
                                                value={
                                                    item.availableUnits?.[0]
                                                        ? `${item.availableUnits[0].name}${item.availableUnits[0].abbreviation ? ` (${item.availableUnits[0].abbreviation})` : ''}`
                                                        : ''
                                                }
                                                placeholder="-"
                                                disabled
                                                readOnly
                                            />
                                        </Col>
                                    )}

                                    <Col md={1}>
                                        {index > 0 && (
                                            <Button
                                                variant="outline-danger"
                                                className="w-100"
                                                onClick={() => removeLine(index)}
                                                title="Remove item"
                                                aria-label="Remove item"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                            )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}

                    <Button variant="outline-secondary" onClick={addLine}>+ Add Item</Button>
                    <Form.Group className="mt-3">
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.remarks}
                            onChange={(e) => setForm({...form, remarks: e.target.value})}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="light" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary">{submitLabel}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}